import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpIcon, MicIcon } from 'lucide-react';
import { AnnaAvatar } from '../avatar/AnnaAvatar';
import { ProcessingTag } from '../ui/ProcessingTag';
import { useDb } from '../../hooks/useDb';
import { useSpeechRecognition } from '../../hooks/useSpeechRecognition';
import { avatarThreads } from '../../utils/dbSelectors';
import type { DeskStage } from './useDeskStage';
const EASE = [0.23, 1, 0.32, 1] as const;

/** Left third: Anna (smaller, same living states) and the conversation stream. */
export function ConversationPanel({
  stage
}: {
  stage: DeskStage;
}) {
  const threads = useDb(avatarThreads);
  const suggestions = useDb(t => t.deskSuggestions);
  const [value, setValue] = useState('');
  const [note, setNote] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const rec = useSpeechRecognition(text => stage.ask(text, 'stem'));
  const {
    setListening
  } = stage;
  useEffect(() => setListening(rec.listening), [rec.listening, setListening]);
  useEffect(() => {
    if (rec.error) setNote(rec.error === 'no-speech' ? 'Ik hoorde niets. Probeer het nog eens.' : 'Microfoon werkt hier niet. Typ je vraag.');
  }, [rec.error]);
  useEffect(() => {
    listRef.current?.scrollTo({
      top: listRef.current.scrollHeight,
      behavior: 'smooth'
    });
  }, [stage.stream.length, stage.revealed?.words]);
  const submit = (text: string) => {
    const t = text.trim();
    if (!t) return;
    setValue('');
    setNote(null);
    stage.ask(t, 'getypt');
  };
  const onMic = () => {
    if (rec.listening) return rec.stop();
    stage.bargeIn();
    setNote(null);
    if (!rec.start()) setNote('Spraakherkenning werkt niet in deze browser. Typ je vraag.');
  };
  return <section aria-label="Gesprek met Anna" className="flex h-full min-h-[75vh] flex-col xl:min-h-0" data-id="element-573" data-mp="3k8h8fwj4zrqt">
      <div className="flex items-center gap-5" data-id="element-574" data-mp="3f79zvv966oj3">
        <div className="aspect-square w-[180px] shrink-0 2xl:w-[230px]" data-id="element-575" data-mp="3fao4791e97t6">
          <AnnaAvatar state={stage.avatarState} threads={threads} pulseKey={stage.pulseKey} data-id="element-576" data-mp="279e2hviy0llw" />
        </div>
        <div className="min-w-0" data-id="element-577" data-mp="2b9oebotf268o">
          <p className="font-display text-[26px] font-light text-ink" data-id="element-578" data-mp="1xayisdm8wemf">Anna</p>
          <p className="text-[15px] text-ink-soft" data-id="element-579" data-mp="1xd2kqq0l33k2">
            {stage.avatarState === 'spreekt' ? 'spreekt' : stage.avatarState === 'denkt' ? 'denkt na' : stage.avatarState === 'luistert' ? 'luistert' : stage.avatarState === 'bezorgd' ? 'let op' : stage.avatarState === 'gestopt' ? 'gestopt' : 'werkt mee'}
          </p>
          <AnimatePresence data-id="element-580" data-mp="1jgxfd6kcm3fq">
            {stage.activity === 'thinking' && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.15
          }} className="mt-2" data-id="element-581" data-mp="14mswhmfjmhkh">
                <ProcessingTag where={stage.thinkingWhere} data-id="element-582" data-mp="pysm70tfhwdh" />
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>

      <div ref={listRef} className="no-scrollbar mt-4 min-h-[200px] flex-1 basis-0 space-y-5 overflow-y-auto pr-1" aria-live="polite" data-id="element-583" data-mp="lzjqhqetvofa">
        {stage.stream.map(line => {
        const isLive = stage.revealed?.id === line.id;
        const words = line.text.split(' ');
        return <motion.div key={line.id} initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.2,
          ease: EASE
        }} className={line.from === 'jij' ? 'pl-10' : ''} data-id="element-584" data-mp="18wd7jxwzictc">
              {line.from === 'anna' ? <p className="font-display text-[21px] font-light leading-snug text-ink 2xl:text-[23px]" data-id="element-585" data-mp="1dr95feowjued">
                  {isLive ? <>
                      {words.slice(0, stage.revealed?.words ?? 0).join(' ')}
                      <span className="sr-only" data-id="element-586" data-mp="3h6l1rfe3uqv3">{words.slice(stage.revealed?.words ?? 0).join(' ')}</span>
                    </> : line.text}
                </p> : <div className="rounded-2xl bg-white/[0.05] px-4 py-3" data-id="element-587" data-mp="3qwecke6fb1ya">
                  <p className="text-[15px] text-ink" data-id="element-588" data-mp="3vteb840y12fd">{line.text}</p>
                  <p className="mt-1 text-[13px] text-ink-muted" data-id="element-589" data-mp="2y7gxejxidl9r">
                    jij · {line.via} · {line.time}
                  </p>
                </div>}
            </motion.div>;
      })}
        {rec.listening && <p className="pl-10 text-[15px] italic text-ink-soft" data-id="element-590" data-mp="383jz5oog7hw6">{rec.interim || 'Ik luister…'}</p>}
      </div>

      <div className="mt-4" data-id="element-591" data-mp="3d5j2iyk6uet4">
        <ul className="mb-3 flex flex-wrap gap-2" aria-label="Suggesties" data-id="element-592" data-mp="newa6m9f6n59">
          {suggestions.map(s => <li key={s} className="min-w-0 max-w-full" data-id="element-593" data-mp="sdqsln3h69ou">
              <button type="button" onClick={() => submit(s)} className="max-w-full rounded-full border border-white/10 px-3 py-1.5 text-left text-[13px] leading-snug text-ink-soft transition-colors duration-150 hover:border-white/25 hover:text-ink" data-id="element-594" data-mp="sc5wvu5fckpe">
                {s}
              </button>
            </li>)}
        </ul>
        <form onSubmit={e => {
        e.preventDefault();
        submit(value);
      }} className="glass flex items-center gap-2 rounded-full py-1.5 pl-5 pr-1.5" data-id="element-595" data-mp="1y6vcjrq4u9in">
          <label htmlFor="desk-ask" className="sr-only" data-id="element-596" data-mp="1p8jpu0sdzvvh">Vraag Anna iets</label>
          <input id="desk-ask" value={value} onChange={e => setValue(e.target.value)} placeholder="Vraag Anna iets…" autoComplete="off" className="min-w-0 flex-1 bg-transparent text-[16px] text-ink placeholder:text-ink-muted focus:outline-none" data-id="element-597" data-mp="1k7vwv9kll4ym" />
          <button type="button" onClick={onMic} aria-pressed={rec.listening} aria-label={rec.listening ? 'Stop met luisteren' : 'Praat met Anna'} className={`grid h-11 w-11 shrink-0 place-items-center rounded-full transition-colors duration-150 ${rec.listening ? 'bg-accent text-night-950' : 'text-ink-soft hover:bg-white/10 hover:text-ink'}`} data-id="element-598" data-mp="39khcm7xj5viy">
            <MicIcon className="h-5 w-5" data-id="element-599" data-mp="1qonp4y06khq3" />
          </button>
          <button type="submit" aria-label="Versturen" disabled={!value.trim()} className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-accent text-night-950 transition-opacity duration-150 disabled:opacity-40" data-id="element-600" data-mp="1vmp5vca4w4ye">
            <ArrowUpIcon className="h-5 w-5" strokeWidth={2.25} data-id="element-601" data-mp="1lqcewm5z457c" />
          </button>
        </form>
        {note && <p className="mt-2 text-[13px] text-amber" data-id="element-602" data-mp="a9coxx73m2gh">{note}</p>}
      </div>
    </section>;
}